import { action } from '@ember/object';
import Component from '@glimmer/component';

import FloatingOverlayManager from 'admin-panel/utils/wysiwyg-editor/floating-overlay';

import type {
  WysiwygAlignment,
  WysiwygDropdownInlineStyle,
  WysiwygEditorDropdownMenu,
  WysiwygEditorLinkPopupConfig,
  WysiwygEditorLinkTargetKind,
  WysiwygInsertItem,
  WysiwygParagraphStyle,
} from 'admin-panel/utils/wysiwyg-editor/types';

interface UiInputGroupWysiwygEditorToolbarSignature {
  Element: HTMLDivElement;
  Args: {
    Named: {
      editorId: string;
      isToolbarShadowVisible: boolean;
      canUndo: boolean;
      undo: () => void;
      canRedo: boolean;
      redo: () => void;
      isHtmlMode: boolean;
      isFormatTriggerActive: boolean;
      showFormatMenu: boolean;
      toggleMenu: (menu: WysiwygEditorDropdownMenu) => void;
      isBoldSelected: boolean;
      isItalicSelected: boolean;
      isUnderlineSelected: boolean;
      isCodeSelected: boolean;
      toggleInlineStyle: (style: WysiwygDropdownInlineStyle) => void;
      alignment: WysiwygAlignment;
      selectAlignment: (alignment: WysiwygAlignment) => void;
      paragraphStyle: WysiwygParagraphStyle;
      selectParagraphStyle: (paragraphStyle: WysiwygParagraphStyle) => void;
      isLinkTriggerActive: boolean;
      showLinkMenu: boolean;
      openLinkPopup: (kind: WysiwygEditorLinkTargetKind) => void;
      activeLinkPopupConfig: WysiwygEditorLinkPopupConfig | null;
      reopenLinkMenu: () => void;
      updateLinkField: (field: string, event: Event) => void;
      registerLinkPopupInput: (field: string, element: Element) => void;
      showRemoveLinkAction: boolean;
      removeWebsiteLink: () => void;
      retryLinkDestinations: () => void;
      isLinkPopupSubmitDisabled: boolean;
      applyLinkPopup: () => void;
      isInsertTriggerActive: boolean;
      showInsertMenu: boolean;
      showAnchorInsertPopup: boolean;
      closeMenu: () => void;
      insertItem: (item: WysiwygInsertItem) => void;
      anchorName: string;
      registerAnchorPopupInput: (element: Element) => void;
      updateAnchorName: (event: Event) => void;
      isAnchorInsertSubmitDisabled: boolean;
      applyAnchorInsertPopup: () => void;
      anchorPopupSubmitLabel: string;
      captureHtmlSelection: (event: MouseEvent) => void;
      toggleHtmlMode: (event: MouseEvent) => void;
    };
  };
}

export default class UiInputGroupWysiwygEditorToolbarComponent extends Component<UiInputGroupWysiwygEditorToolbarSignature> {
  private overlay = new FloatingOverlayManager();
  private linkPopupHeading?: HTMLElement;

  @action
  handleKeydown(event: KeyboardEvent) {
    const toolbar = event.currentTarget;
    const target = event.target;

    if (!(toolbar instanceof HTMLElement) || !(target instanceof Element)) {
      return;
    }

    const currentControl = target.closest<HTMLElement>(
      '[data-wysiwyg-toolbar-control]'
    );

    if (!currentControl || !toolbar.contains(currentControl)) {
      return;
    }

    const controls = Array.from(
      toolbar.querySelectorAll<HTMLElement>(
        '[data-wysiwyg-toolbar-control]:not([disabled])'
      )
    );
    const currentIndex = controls.indexOf(currentControl);
    let nextIndex: number | undefined;

    if (currentIndex === -1 || controls.length === 0) {
      return;
    }

    switch (event.key) {
      case 'ArrowLeft':
        nextIndex = (currentIndex - 1 + controls.length) % controls.length;
        break;
      case 'ArrowRight':
        nextIndex = (currentIndex + 1) % controls.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = controls.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    event.stopPropagation();
    controls[nextIndex]?.focus({ preventScroll: true });
  }

  @action
  registerOverlay(element: Element) {
    if (!(element instanceof HTMLElement)) {
      return;
    }

    const trigger = element.parentElement?.querySelector(
      '[data-wysiwyg-trigger]'
    );

    if (!(trigger instanceof HTMLElement)) {
      return;
    }

    this.overlay.show(
      element,
      trigger.getBoundingClientRect(),
      trigger,
      this.args.closeMenu,
      { restoreFocusTo: trigger }
    );
  }

  @action
  unregisterOverlay(element: Element) {
    if (element instanceof HTMLElement) {
      this.overlay.unregister(element);
    }
  }

  @action
  registerLinkPopupHeading(element: Element) {
    if (element instanceof HTMLElement) {
      this.linkPopupHeading = element;
    }
  }

  @action
  unregisterLinkPopupHeading(element: Element) {
    if (this.linkPopupHeading === element) {
      this.linkPopupHeading = undefined;
    }
  }

  @action
  retryLinkDestinations() {
    const heading = this.linkPopupHeading;

    if (heading) {
      heading.dataset['wysiwygRetryFocus'] = '';
      heading.addEventListener(
        'blur',
        () => delete heading.dataset['wysiwygRetryFocus'],
        { once: true }
      );
      heading.focus({ preventScroll: true });
    }

    this.args.retryLinkDestinations();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::InputGroup::WysiwygEditor::Toolbar': typeof UiInputGroupWysiwygEditorToolbarComponent;
    'ui/input-group/wysiwyg-editor/toolbar': typeof UiInputGroupWysiwygEditorToolbarComponent;
  }
}
